import { useEffect, useLayoutEffect, useRef, useState } from "react";

import { countFittingValues, type FitMeasurement } from "../fittingValues";

import { isOverflowing } from "@/lib/utils/isOverflowing";

const measure = (text: HTMLElement, values: HTMLElement): FitMeasurement => {
  const left = text.getBoundingClientRect().left;
  return {
    valueRightEdges: Array.from(values.children).map(
      (value) => value.getBoundingClientRect().right - left,
    ),
    availableWidth: text.clientWidth,
    isOverflowing: isOverflowing(text),
  };
};

export function useFittingValueCount(values: string[], enabled: boolean) {
  const textRef = useRef<HTMLSpanElement>(null);
  const valuesRef = useRef<HTMLSpanElement>(null);
  const [fit, setFit] = useState<{ key: string; count: number } | null>(null);
  const [remeasureTick, setRemeasureTick] = useState(0);

  const measureKey = JSON.stringify([values, remeasureTick]);
  const isMeasuring = enabled && fit?.key !== measureKey;
  const fittingCount = isMeasuring || !fit ? values.length : fit.count;

  useLayoutEffect(() => {
    if (!isMeasuring || !textRef.current || !valuesRef.current) return;
    setFit({
      key: measureKey,
      count: countFittingValues(measure(textRef.current, valuesRef.current)),
    });
  }, [isMeasuring, measureKey]);

  useEffect(() => {
    const text = textRef.current;
    if (!enabled || !text) return;
    const remeasure = () => setRemeasureTick((tick) => tick + 1);
    const remeasureWhenCutOff = () => {
      if (fittingCount > 1 && isOverflowing(text)) remeasure();
    };
    const observer = new ResizeObserver(remeasureWhenCutOff);
    observer.observe(text);
    window.addEventListener("resize", remeasure);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", remeasure);
    };
  }, [enabled, fittingCount]);

  return { textRef, valuesRef, isMeasuring, fittingCount };
}
